import { TypedArrayUtils } from '@seddi/core';
import { Seam } from '@seddi/geometry';
import {
  type GarmentGeometry,
  type IPieceGeometry,
  type IScene,
  type ISceneEntity,
  type ISeamComponent,
  type ISeamSide,
  type SeamProcess,
  GarmentEntities,
  PiecesLinesPaths,
  StitchOrder,
  meshOrientedSpline,
} from '@seddi/scene';
import { SeamBuilder } from '@seddi/scene-builder';
import { type Object3D, BufferGeometry, LineBasicMaterial, LineSegments } from '@seddi/three';

import { createVertexAttributeF32, updateVertexAttributeF32 } from '../core/bufferGeometry';
import type { ResourcesContext } from '../resources/resourcesContext';
import { SceneGeometryModule } from '../scene/sceneGeometryModule';
import { selectColorByIndex } from '../theme/themeColors';
import type { Theme } from '../theme/themeTypes';
import { type SeamTapeRenderable, dispose as disposeTape } from './seamTapeRenderable';
import {
  type StitchRenderable,
  create as createStitch,
  dispose as disposeStitch,
} from './stitchRenderable';

interface Stitch extends StitchRenderable {
  name: string;
  order: StitchOrder;
}

export interface SeamRenderable {
  /** Entity ID */
  readonly id: string;
  geometry: BufferGeometry;
  garmentGeometry?: GarmentGeometry;
  readonly material: LineBasicMaterial;
  readonly mesh: LineSegments;
  readonly stitches: Stitch[];
  tape?: SeamTapeRenderable;
  // Use stitches decals
  /** Use stitches decals */
  readonly useStitchesDecals: boolean;
}

/**
 * Create renderable.
 * @param id - ID.
 * @param node - Node.
 */
export const create = (
  id: string,
  node: Object3D,
  seam: ISeamComponent,
  theme: Theme,
  useStitchesDecals: boolean,
): SeamRenderable => {
  const geometry = new BufferGeometry();
  // Material.
  const color = selectColorByIndex(theme.threads, seam.color);
  const material = new LineBasicMaterial();
  material.color.set(color).convertSRGBToLinear();
  // Mesh.
  const mesh = new LineSegments(geometry, material);
  mesh.name = `${id}-mesh`;
  node.add(mesh);

  return { geometry, id, material, mesh, stitches: [], useStitchesDecals };
};

/**
 * Dispose.
 * @param self - Renderable.
 * @param resources - Resources context.
 */
export const dispose = (self: SeamRenderable, resources: ResourcesContext) => {
  disposeStitches(self);
  if (self.tape) {
    disposeTape(self.tape, resources);
  }
  self.geometry.dispose();
  self.material.dispose();
  const mesh = self.mesh;
  mesh.parent.remove(mesh);
};

/**
 * Vertices positions for seam glue, lines are drawn between pairs of vertices.
 * Future improvement could be to generate index buffer data and avoid vertex duplicates.
 * @param sides Seam sides vertices.
 * @returns LineSegments vertices.
 */
const verticesDataGlue = (sides: Float32Array[][]): Float32Array => {
  const side0 = sides[0];
  const side1 = sides[1];
  const length = side0.reduce((n, segments) => n + segments.length, 0) * 2;
  const data = new Float32Array(length);
  let offset = 0;

  for (let s = 0; s < side0.length; s++) {
    const vertices0 = side0[s];
    const vertices1 = side1[s];

    for (let i = 0; i < vertices0.length; i += 3) {
      data[offset] = vertices0[i];
      data[offset + 1] = vertices0[i + 1];
      data[offset + 2] = vertices0[i + 2];
      data[offset + 3] = vertices1[i];
      data[offset + 4] = vertices1[i + 1];
      data[offset + 5] = vertices1[i + 2];
      offset += 6;
    }
  }

  return data;
};

/**
 * Vertices positions for seam sides, lines are drawn between pairs of vertices.
 * Future improvement could be to generate index buffer data and avoid vertex duplicates.
 * @param sides Seam sides vertices.
 * @returns LineSegments vertices.
 */
const verticesDataSides = (sides: Float32Array[][]): Float32Array => {
  let length = 0;

  for (const segments of sides) {
    for (const vertices of segments) {
      length += vertices.length * 2 - 6;
    }
  }

  const data = new Float32Array(length);
  let offset = 0;

  for (const segments of sides) {
    for (const vertices of segments) {
      for (let i = 0; i < vertices.length - 3; i += 3) {
        data[offset] = vertices[i];
        data[offset + 1] = vertices[i + 1];
        data[offset + 2] = vertices[i + 2];
        data[offset + 3] = vertices[i + 3];
        data[offset + 4] = vertices[i + 4];
        data[offset + 5] = vertices[i + 5];
        offset += 6;
      }
    }
  }

  return data;
};

/**
 * Updates mesh geometry vertex buffers from sides vertices.
 * @param mesh Mesh.
 * @param sides Sides vertices.
 */
const createMeshGeometry = (mesh: LineSegments, sides: Float32Array[][]) => {
  // Positions from sides.
  let position = verticesDataSides(sides);

  // Positions from glue.
  if (sides.length > 1) {
    const positionGlue = verticesDataGlue(sides);
    position = TypedArrayUtils.Concat([position, positionGlue]);
  }

  const geometry = mesh.geometry as BufferGeometry;
  createVertexAttributeF32(geometry, 'position', position, 3);
  geometry.computeBoundingSphere();
};

/**
 * Updates mesh geometry vertex buffers from sides vertices.
 * @param mesh Mesh.
 * @param sides Sides vertices.
 */
const updateMeshGeometry = (mesh: LineSegments, sides: Float32Array[][]) => {
  // Positions from sides.
  let position = verticesDataSides(sides);

  // Positions from glue.
  if (sides.length > 1) {
    const positionGlue = verticesDataGlue(sides);
    position = TypedArrayUtils.Concat([position, positionGlue]);
  }

  const geometry = mesh.geometry as BufferGeometry;
  updateVertexAttributeF32(geometry, 'position', position, 3);
  geometry.computeBoundingSphere();
};

/**
 * Construct seam vertices from piece triangulation.
 * @param sides Seam sides.
 * @param pieces Seam pieces.
 * @param resolution Scene resolution.
 */
const sidesVertices = (
  sides: ISeamSide[],
  pieces: ISceneEntity[],
  resolution: number = 0.01,
): Float32Array[][] => {
  const cellSize = resolution * 1.5;

  const geometryCache = SceneGeometryModule.instance.geometryCache;
  const geometryMap = new Map<string, IPieceGeometry>();

  for (const piece of pieces) {
    const {
      _id,
      components: { garmentPiece },
    } = piece;

    // Update 2D query acceleration structures.
    const pieceCache = geometryCache.touchPiece(_id);
    pieceCache.updateGrid(
      garmentPiece.geometry.indices,
      garmentPiece.geometry.position2D,
      cellSize,
    );

    geometryMap.set(_id, garmentPiece.geometry);
  }

  // Construct seam.
  const paths = new PiecesLinesPaths();
  paths.build(pieces);
  const seam = new Seam();
  const builder = new SeamBuilder(paths);
  builder.build(sides, seam);

  const vertices = [];

  for (const side of seam.sides) {
    const sideVertices = [];

    for (const segment of side.segments) {
      const { direction, length, path, piece } = segment as any;

      // Sample vertices from seam sides.
      const pieceCache = geometryCache.pieces.get(piece);
      const pieceGeometry = geometryMap.get(piece);
      const is = pieceGeometry.indices;
      const vs = pieceGeometry.position3D;

      const nSegments = Math.floor((length + 0.01 - 1e-6) / 0.01);
      const h = 1 / nSegments;
      const segmVertices = new Float32Array((nSegments + 1) * 3);

      for (let i = 0; i <= nSegments; i++) {
        // Sample path by distance.
        const pt = path.normalizedDistanceToPoint(h * i);
        const p2 = path.eval(pt);

        // Accelerated point inside triangle-mesh query.
        const res = pieceCache.grid.query(p2);
        const { triangle: f, coordinates: c } = res;

        // Reverse output position if direction is false.
        const o = direction ? i * 3 : (nSegments - i) * 3;

        // Apply interpolation.
        const f3 = f * 3;
        const i0 = is[f3 + 0] * 3;
        const i1 = is[f3 + 1] * 3;
        const i2 = is[f3 + 2] * 3;
        segmVertices[o + 0] = c.x * vs[i0 + 0] + c.y * vs[i1 + 0] + c.z * vs[i2 + 0];
        segmVertices[o + 1] = c.x * vs[i0 + 1] + c.y * vs[i1 + 1] + c.z * vs[i2 + 1];
        segmVertices[o + 2] = c.x * vs[i0 + 2] + c.y * vs[i1 + 2] + c.z * vs[i2 + 2];
      }

      sideVertices.push(segmVertices);
    }

    vertices.push(sideVertices);
  }

  return vertices;
};

/**
 * Copy vertices position from indices.
 * @param indices Piece geometry indices.
 * @param positions Piece geometry positions.
 * @param vertexOffset defines the vertex offset ??.
 * @returns the array of positions.
 */
const copyVerticesFromIndices = (indices: Uint32Array, positions: Float32Array): Float32Array => {
  const n = indices.length;
  const vertices = new Float32Array(n * 3);

  for (let i = 0; i < n; i += 1) {
    const v = indices[i];
    const v3 = v * 3;
    const i3 = i * 3;
    vertices[i3] = positions[v3];
    vertices[i3 + 1] = positions[v3 + 1];
    vertices[i3 + 2] = positions[v3 + 2];
  }

  return vertices;
};

/**
 * Update seam geometry from high-resolution geometry.
 * @param self - Renderable.
 * @param garmentGeometry - Garment geometry.
 */
export const updateGeometryFromHiRes = (self: SeamRenderable, garmentGeometry: GarmentGeometry) => {
  const id = self.id;
  const op = garmentGeometry.sewingOperations.find((o) => o.id === id)!;
  const sides = op.lines.map((side) => {
    return side.segments.map((segment) => {
      const { indices, position3D } = segment;
      return copyVerticesFromIndices(indices, position3D);
    });
  });
  // Create mesh.
  createMeshGeometry(self.mesh, sides);
  // Save origin.
  self.garmentGeometry = garmentGeometry;
};

/**
 * Update seam geometry from scene.
 * @param self - Renderable.
 * @param seam - Seam.
 * @param scene - Scene.
 */
export const updateGeometryFromLoRes = (
  self: SeamRenderable,
  seam: ISeamComponent,
  scene: IScene,
) => {
  const pieces = GarmentEntities.PiecesFromSeamSides(scene.children, seam.lines);
  // NOTE: Batch updates should guarantee this never happens.
  if (!pieces) {
    return;
  }
  const sides = sidesVertices(seam.lines, pieces, scene.resolution);
  // Update dynamic mesh.
  updateMeshGeometry(self.mesh, sides);
  // Save origin.
  self.garmentGeometry = undefined;
};

/**
 * Updates renderable geometry.
 * @param self - Renderable.
 * @param seam - Parent.
 * @param scene - Scene.
 * @param garmentGeometry - Garment geometry.
 */
export const updateGeometry = (
  self: SeamRenderable,
  seam: ISeamComponent,
  scene: IScene,
  garmentGeometry: GarmentGeometry | undefined,
): boolean => {
  if (garmentGeometry) {
    updateGeometryFromHiRes(self, garmentGeometry);
  } else {
    updateGeometryFromLoRes(self, seam, scene);
  }
  return true;
};

/**
 * Create stitches.
 * @param self - Renderable.
 * @param process - Process.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
export const createStitches = (
  self: SeamRenderable,
  process: SeamProcess,
  garmentGeometry: GarmentGeometry,
  resources: ResourcesContext,
) => {
  // do not create stitches when relying on decals.
  if (self.useStitchesDecals) {
    return;
  }
  const sewingOp = garmentGeometry.sewingOperations.find((op) => op.id === self.id);
  for (const sewingOpStitch of sewingOp.stitches) {
    const { name, order } = sewingOpStitch;
    const processStitch = process[stitchOrderToProp(order)];
    const color = processStitch[processStitch.kind][name].color;
    const geometryData = meshOrientedSpline(sewingOpStitch, 3);
    const stitch = createStitch(self.mesh.parent, geometryData, color, resources);
    stitch.mesh.name = `${self.id}-stitch-${order}-${name}`;
    self.stitches.push({ ...stitch, name, order });
  }
};

/**
 * Dispose stitches.
 * @param self - Renderable.
 */
export const disposeStitches = (self: SeamRenderable) => {
  for (const stitch of self.stitches) {
    disposeStitch(stitch);
  }
  self.stitches.length = 0;
};

const stitchOrderToProp = (order: StitchOrder): 'stitch' | 'stitchSec' => {
  if (order === StitchOrder.SECONDARY) {
    return 'stitchSec';
  }
  return 'stitch';
};
